import {Injectable}          from '@angular/core';
import {IStateStore}         from '../../ap-interface';
import {Store}               from '../index';
import {ApSignalrService}    from '../../ap-core/services/ap-signalr.service';
import {
  ApCustomTypes,
  LabResultAttachmentDelete,
  LabResultAttachmentDownload,
  LabResultAttachmentDownloadFail,
  LabResultAttachmentDownloadSuccess,
  LabResultAttachmentLoad,
  LabResultAttachmentLoadFail,
  LabResultAttachmentLoadSuccess,
  LabResultAttachmentUpload,
  LabResultAttachmentUploadFail,
  LabResultAttachmentUploadSuccess,
  NetTypes
}                            from 'invoker-transport';
import {SafeBehaviorSubject} from 'ts-tooling';
import IGuid = System.IGuid;
import ILabResultAttachment = Data.Dpl3.ILabResultAttachment;

interface ILabResultAttachmentStore extends IStateStore<ILabResultAttachment> {
}

@Injectable({providedIn: 'root'})
export class LabResultAttachmentStore extends Store<ILabResultAttachmentStore> {
  constructor(public backend: ApSignalrService) {
    super(backend, {
      loaded: false,
      loading: false,
      data: []
    });
    backend.registerObservable(LabResultAttachmentLoadSuccess).subscribe(d => {
      super.Mutate(s => s.data, () => d.Data);
      super.SetLoadFinishState();
    });
    backend.registerObservable(LabResultAttachmentLoadFail).subscribe(_ => {
      super.SetLoadFailState();
    });
    this.AfterDatabaseUpdate.subscribe(() => this.ReloadSource());
  }

  /**
   * Observable for tracking the loading state of lab result attachments
   * @returns SafeBehaviorSubject emitting boolean loading state
   */
  public get Loading$(): SafeBehaviorSubject<boolean> {
    return super.Listen(s => s.loading);
  }

  /**
   * Observable for accessing the current list of lab result attachments
   * @returns SafeBehaviorSubject emitting array of ILabResultAttachment objects
   */
  public get LabResultAttachments$(): SafeBehaviorSubject<ILabResultAttachment[]> {
    return this.Listen((s) => s.data);
  }

  /**
   * Initiates loading of lab result attachments from the backend
   * Sets loading state and dispatches load request
   */
  public loadLabResultAttachments(): void {
    super.SetLoadState();
    this.DispatchBackend(new LabResultAttachmentLoad([]));
  }

  /**
   * Requests deletion of a specific lab result attachment
   * @param attachmentId - Unique identifier of the attachment to delete
   * @public
   */
  public deleteLabResultAttachment(attachmentId: IGuid): void {
    this.DispatchBackend(new LabResultAttachmentDelete([
      {Name: 'attachmentId', Type: NetTypes.GUID, Value: attachmentId}
    ]));
  }

  /**
   * Uploads a new lab result attachment to the backend
   * @param attachment - Lab result attachment data to upload
   * @returns Promise resolving on successful upload, rejecting on failure
   * @public
   */
  public uploadLabResultAttachment(attachment: ILabResultAttachment): Promise<void> {
    return new Promise((resolve, reject) => {
      this.DispatchBackend(new LabResultAttachmentUpload([
        {Name: 'attachment', Type: ApCustomTypes.Data_Dpl3_LabResultAttachment, Value: attachment}
      ])).watchStream({
        action: LabResultAttachmentUploadSuccess,
        todo: () => resolve()
      }).watchStream({
        action: LabResultAttachmentUploadFail,
        todo: _ => reject()
      });
    });
  }

  /**
   * Downloads a specific lab result attachment by it`s Id
   * @param attachmentId - Unique identifier of the attachment to download
   * @returns Promise resolving with attachment data on successful download, rejecting on failure
   * @public
   */
  public downloadLabResultAttachment(attachmentId: IGuid): Promise<ILabResultAttachment> {
    return new Promise((resolve, reject) => {
      this.DispatchBackend(new LabResultAttachmentDownload([
        {Name: 'attachmentId', Type: NetTypes.GUID, Value: attachmentId}
      ])).watchStream({
        action: LabResultAttachmentDownloadSuccess,
        todo: (result: any) => resolve(result.Data as ILabResultAttachment)
      }).watchStream({
        action: LabResultAttachmentDownloadFail,
        todo: _ => reject()
      });
    });
  }
}
